import React, { Fragment } from 'react'
import { Teacher } from '../providers/context'
import { TextInput, CharcoalInput } from '../components/form/textInput'
import { IconSearch } from '../icons/library'
import { ThreePanelLayout } from '../layouts/threePanel'
import { ListButton, ListButtonCheckbox } from '../components/simple/listButton'
import { formatDateRange, SimpleDateRange, SimpleDateRangePicker } from '../components/form/datePicker'
import { ControlledModal, ModalTitle } from '../layouts/components/modal'
import { ExportWidget } from './dashboard/widgets'
import { LocalTable, RemoteTable } from '../components/table'
import { ColumnDef } from '@tanstack/react-table'
import { LineChart, LineChartData } from '../components/charts/lineChart'
import { useRequest } from '../request'
import { Loader } from '../components/simple/loader'
import { Link } from 'react-router-dom'

type School = {
  id: number
  name: string
}

type AbsencePerDay = {
  date: string // ISO date string
  excused_count: number
  total_count: number
}

type Absence = {
  student_id: number
  sync_id: string
  fname: string
  lname: string
  grade: string
  uniqueExcusedAbsenceDays: number
  uniqueAbsenceDays: number
  totalAbsences: number
  totalExcused: number
}

type AbsenceReport = {
  schools: School[]
  absenceDays: number
  absencesPerDay: AbsencePerDay[]
}

export const AttendanceStatsPage: React.FC<{ user: Teacher }> = ({ user }) => {
  const [search, setSearch] = React.useState('')
  const [filteredSchools, setFilteredSchools] = React.useState(new Set<number>())
  const [range, setRange] = React.useState<SimpleDateRange>('month')
  const [showExportModal, setShowExportModal] = React.useState(false)

  // Fetch chart data and schools (lightweight)
  const { data: reportData, loading: reportLoading } = useRequest<AbsenceReport>(
    '/stats/frontend_api_absenceStatsPaginated',
    React.useMemo(
      () => ({
        timerange: formatDateRange(range),
        schools: Array.from(filteredSchools),
        chartOnly: true, // Add this parameter to only fetch chart data
      }),
      [range, filteredSchools]
    )
  )

  const allSchools = React.useMemo(() => {
    if (!reportData) return []
    return reportData.schools.map((school) => ({
      id: school.id,
      name: school.name,
    }))
  }, [reportData])

  const chartData: LineChartData | undefined = React.useMemo(() => {
    if (!reportData?.absencesPerDay) return undefined

    return {
      labels: reportData.absencesPerDay.map((d) => d.date),
      datasets: [
        {
          label: 'Excused Absences',
          color: '#4F8A8B',
          data: reportData.absencesPerDay.map((d) => d.excused_count),
        },
        {
          label: 'Total Absences',
          color: '#F45B69',
          data: reportData.absencesPerDay.map((d) => d.total_count),
        },
      ],
      xLabel: 'Date',
      yLabel: 'Absences',
    }
  }, [reportData])

  const remoteTableData = React.useMemo(
    () => ({
      timerange: formatDateRange(range),
      schools: Array.from(filteredSchools),
    }),
    [range, filteredSchools]
  )

  const columns: ColumnDef<Absence>[] = React.useMemo(
    () => [
      {
        header: 'Student ID',
        accessorKey: 'student_id',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Link to={`/stats/attendance/student/${row.original.student_id}`}>{row.original.student_id}</Link>
          </div>
        ),
      },
      {
        header: 'First Name',
        accessorKey: 'fname',
        cell: ({ row }) => <div className="flex items-center gap-2">{row.original.fname}</div>,
      },
      {
        header: 'Last Name',
        accessorKey: 'lname',
        cell: ({ row }) => <div className="flex items-center gap-2">{row.original.lname}</div>,
      },
      {
        header: 'Grade',
        accessorKey: 'grade',
      },
      {
        header: 'Absence Days',
        accessorKey: 'uniqueAbsenceDays',
      },
      {
        header: 'Excused Days',
        accessorKey: 'uniqueExcusedAbsenceDays',
      },
      {
        header: 'Total Absences',
        accessorKey: 'totalAbsences',
      },
    ],
    []
  )

  const onToggleSchool = React.useCallback(
    (school: number) => {
      const schools = new Set(filteredSchools)
      if (!schools.has(school)) schools.add(school)
      else schools.delete(school)
      setFilteredSchools(schools)
    },
    [filteredSchools]
  )

  return (
    <ThreePanelLayout
      noMobile
      user={user}
      left={
        <>
          <TextInput
            className={`rounded-full outline outline-1 outline-charcoal-text ${CharcoalInput}`}
            value={search}
            onChange={setSearch}
            placeholder="Search"
            icon={<IconSearch />}
          />
          <div className="ml-2 flex items-center justify-between gap-2">
            Time Range:
            <SimpleDateRangePicker
              className="!border-none bg-charcoal-active !ring-0"
              range={range}
              onRangeChange={setRange}
            />
          </div>
          {(user.schools.length > 1 || user.districtAccess) && (
            <div className="flex flex-col gap-1">
              <div className="ml-2 mt-2 font-bold">Filter by School</div>
              {allSchools.sort().map((school) => (
                <ListButton key={school.id} onClick={() => onToggleSchool(school.id)}>
                  {school.name}
                  <ListButtonCheckbox checked={filteredSchools.has(school.id)} />
                </ListButton>
              ))}
            </div>
          )}
        </>
      }
      floating={
        <ControlledModal
          noAutoFocus
          open={showExportModal}
          onOpenChange={(o) => !o && setShowExportModal(false)}
          className="!bg-charcoal"
          size="xs"
        >
          <ModalTitle hidden>Export Absensces</ModalTitle>
          <ExportWidget className="!shadow-none" />
        </ControlledModal>
      }
    >
      <div className="flex flex-grow flex-col gap-4 overflow-auto bg-lightBlue p-4">
        <div
          className={`flex flex-col gap-2 rounded bg-white p-4 shadow ${reportLoading && reportData ? 'opacity-50' : ''}`}
        >
          <div className="flex flex-row items-center justify-between gap-4">
            <div className="ml-1 text-xl font-bold">Absences By Date</div>
          </div>
          {chartData ? <LineChart className="min-h-72 px-2" data={chartData} legend /> : <Loader className="my-4" />}
        </div>
        <div className="flex flex-col gap-2 rounded bg-white p-4 shadow">
          <div className="flex flex-row items-center justify-between gap-4">
            <div className="ml-1 text-xl font-bold">Absences By Student</div>
            <TextInput placeholder="Search" value={search} onChange={setSearch} />
          </div>
          <RemoteTable
            className="text-sm"
            columns={columns}
            remote={{
              url: '/stats/frontend_api_absenceStatsPaginated',
              data: remoteTableData,
            }}
            search={search}
            pagination={20}
            exportName="absencesByStudent"
          />
        </div>
      </div>
    </ThreePanelLayout>
  )
}
